import { db } from '../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';

export interface ZoneData {
  zone: number;
  communes: string[];
  price: number;
}

let cachedZonePricing: ZoneData[] | null = null;
let cachedCommunes: string[] | null = null;

// Fetch zone pricing from Firebase
export async function fetchZonePricingFromFirebase(): Promise<ZoneData[]> {
  try {
    console.log('🔥 Fetching zone pricing from Firebase...');
    
    const zonePricingQuery = query(
      collection(db, 'zone_pricing'),
      orderBy('zone')
    );
    
    const snapshot = await getDocs(zonePricingQuery);
    
    if (snapshot.empty) {
      console.warn('⚠️ No zone pricing found in Firebase, using fallback data');
      return getFallbackZonePricing();
    }
    
    const zones = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        zone: data.zone,
        communes: data.communes || [],
        price: data.price || 0,
      } as ZoneData;
    });
    
    console.log(`✅ Successfully fetched ${zones.length} zones from Firebase`);
    
    // Cache the data
    cachedZonePricing = zones;
    cachedCommunes = null; // Reset communes cache
    
    return zones;
  } catch (error) {
    console.error('❌ Error fetching zone pricing from Firebase:', error);
    console.log('📋 Using fallback zone pricing data');
    return getFallbackZonePricing();
  }
}

// Get zone pricing (with caching)
export async function getZonePricing(): Promise<ZoneData[]> {
  if (cachedZonePricing) {
    console.log('📦 Using cached zone pricing data');
    return cachedZonePricing;
  }
  
  return await fetchZonePricingFromFirebase();
}

// Get all communes (with caching)
export async function getCommunes(): Promise<string[]> {
  if (cachedCommunes) {
    console.log('📦 Using cached communes data');
    return cachedCommunes;
  }
  
  const zones = await getZonePricing();
  const allCommunes: string[] = [];
  
  for (const zone of zones) {
    allCommunes.push(...zone.communes);
  }
  
  const uniqueCommunes = Array.from(new Set(allCommunes)).sort();
  cachedCommunes = uniqueCommunes;
  
  console.log(`✅ Generated ${uniqueCommunes.length} unique communes from zones`);
  return uniqueCommunes;
}

// Clear cache (useful for testing or when data is updated)
export function clearPricingCache(): void {
  cachedZonePricing = null;
  cachedCommunes = null;
  console.log('🧹 Pricing cache cleared');
}

// Find zone for a commune
export async function getZoneForCommune(commune: string): Promise<number> {
  const zones = await getZonePricing();
  const zone = zones.find(z => 
    z.communes.some(c => c.toLowerCase() === commune.toLowerCase())
  );
  return zone ? zone.zone : 5; // Default to zone 5 if not found
}

// Get price for a zone
export async function getPriceForZone(zone: number): Promise<number> {
  const zones = await getZonePricing();
  const zoneData = zones.find(z => z.zone === zone);
  return zoneData ? zoneData.price : 25000; // Default to highest price if not found
}

// Calculate delivery price
export async function calculateDeliveryPrice(
  deliveryType: 'pickup' | 'dropoff',
  pickupCommune: string,
  dropoffCommune: string
): Promise<number> {
  const dropoffZone = await getZoneForCommune(dropoffCommune);
  const dropoffPrice = await getPriceForZone(dropoffZone);

  if (deliveryType === 'pickup') {
    const pickupZone = await getZoneForCommune(pickupCommune);
    const pickupPrice = await getPriceForZone(pickupZone);
    return pickupPrice + dropoffPrice;
  }

  return dropoffPrice;
}

// Format price
export function formatPrice(price: number): string {
  return `${price.toLocaleString('fr-FR').replace(/,/g, ' ')} FC`;
}

// Fallback data (same as the original hardcoded data)
function getFallbackZonePricing(): ZoneData[] {
  return [
    {
      zone: 1,
      communes: ['Gombe', 'Lingwala', 'Kinshasa', 'Kintambo'],
      price: 13000
    },
    {
      zone: 2,
      communes: ['Barumbu', 'Bandalungwa', 'Kasa-Vubu', 'Ngiri-Ngiri'],
      price: 16000
    },
    {
      zone: 3,
      communes: ['Ngaliema', 'Limete', 'Kalamu', 'Selembao', 'Makala'],
      price: 18000
    },
    {
      zone: 4,
      communes: ['Bumbu', 'Kimbanseke', 'Lemba', 'Masina', 'Matete', 'Kisenso', 'Ngaba'],
      price: 20000
    },
    {
      zone: 5,
      communes: ['Mont-Ngafula', 'Ndjili', 'Nsele', 'Maluku'],
      price: 25000
    }
  ];
}